import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, XCircle, Filter } from 'lucide-react'
import { useAppStore } from '../../store/useAppStore'
import { adminApi } from './adminApi'

interface TxRow {
  id: string
  ref_code: string
  amount_baht: number
  status: string
  created_at: string
  completed_at: string | null
  avg_confidence: number | null
  consumed: boolean
  user_id: string
  users: { line_uid: string; display_name: string | null } | null
}

interface TxResponse {
  transactions: TxRow[]
  total: number
}

const STATUS_OPTIONS = [
  { value: '', label: 'ทั้งหมด' },
  { value: 'pending_review', label: 'รอตรวจ', color: '#c9a05f' },
  { value: 'completed', label: 'สำเร็จ', color: '#6b8a4f' },
  { value: 'rejected', label: 'ไม่ผ่าน', color: '#b85450' },
  { value: 'awaiting_slip', label: 'รอสลิป', color: '#8a8270' },
  { value: 'expired', label: 'หมดอายุ', color: '#8a8270' },
]

function formatDateTime(s: string): string {
  const d = new Date(s)
  const months = ['ม.ค.','ก.พ.','มี.ค.','เม.ย.','พ.ค.','มิ.ย.','ก.ค.','ส.ค.','ก.ย.','ต.ค.','พ.ย.','ธ.ค.']
  return `${d.getDate()} ${months[d.getMonth()]} ${(d.getFullYear() + 543).toString().slice(-2)} • ${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}`
}

export default function AdminTransactionsPage() {
  const { lineUid } = useAppStore()
  const [statusFilter, setStatusFilter] = useState('pending_review')
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'transactions', lineUid, statusFilter],
    queryFn: () => adminApi.transactions(lineUid!, { status: statusFilter || undefined, limit: 50 }) as Promise<TxResponse>,
    enabled: !!lineUid,
    refetchInterval: 15_000,
  })

  const actionMutation = useMutation({
    mutationFn: ({ txId, action, reason }: { txId: string; action: 'approve' | 'reject'; reason?: string }) =>
      adminApi.txAction(lineUid!, txId, action, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'transactions'] })
    },
  })

  return (
    <div className="p-4 md:p-6 lg:p-8">

      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold mb-1" style={{ color: '#2d2820' }}>
          💳 ธุรกรรม
        </h1>
        <p className="text-sm" style={{ color: '#6b6354' }}>
          ตรวจสอบและจัดการการชำระเงิน (manual review)
        </p>
      </div>

      {/* Filter */}
      <div className="mb-4 flex items-center gap-2 overflow-x-auto pb-2">
        <Filter size={16} style={{ color: '#8a8270' }} className="flex-shrink-0" />
        {STATUS_OPTIONS.map(opt => (
          <button key={opt.value}
                  onClick={() => setStatusFilter(opt.value)}
                  className="px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap"
                  style={{
                    background: statusFilter === opt.value
                      ? (opt.color ?? '#7a8a6a')
                      : '#faf7ef',
                    color: statusFilter === opt.value ? '#fff' : '#6b6354',
                    border: '1px solid rgba(0,0,0,0.06)',
                  }}>
            {opt.label}
          </button>
        ))}
      </div>

      {/* Transactions List */}
      <div className="rounded-2xl overflow-hidden"
           style={{ background: '#faf7ef', border: '1px solid rgba(0,0,0,0.04)' }}>
        {isLoading && (
          <div className="p-8 text-center text-sm" style={{ color: '#8a8270' }}>กำลังโหลด...</div>
        )}

        {data?.transactions.length === 0 && (
          <div className="p-8 text-center text-sm" style={{ color: '#8a8270' }}>
            ไม่มีรายการ
          </div>
        )}

        {data?.transactions.map(tx => (
          <TxCard
            key={tx.id}
            tx={tx}
            onApprove={() => actionMutation.mutate({ txId: tx.id, action: 'approve' })}
            onReject={() => actionMutation.mutate({ txId: tx.id, action: 'reject' })}
            processing={actionMutation.isPending}
          />
        ))}
      </div>
    </div>
  )
}

function TxCard({ tx, onApprove, onReject, processing }: {
  tx: TxRow
  onApprove: () => void
  onReject: () => void
  processing: boolean
}) {
  const status = STATUS_OPTIONS.find(s => s.value === tx.status)
  const isPending = tx.status === 'pending_review'
  const isAwaiting = tx.status === 'awaiting_slip'

  return (
    <div className="p-3 md:p-4 border-b last:border-0"
         style={{ borderColor: 'rgba(0,0,0,0.04)' }}>
      <div className="flex items-start gap-3">

        <div className="flex-1 min-w-0">
          {/* Top: Ref + Status */}
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span className="font-mono font-bold text-sm" style={{ color: '#2d2820' }}>
              {tx.ref_code}
            </span>
            {status && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                    style={{ background: `${status.color}33`, color: status.color }}>
                {status.label}
              </span>
            )}
            {tx.consumed && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full"
                    style={{ background: '#e8eedc', color: '#5d6b50' }}>
                ใช้แล้ว
              </span>
            )}
          </div>

          {/* User + Amount */}
          <div className="text-xs mb-1" style={{ color: '#6b6354' }}>
            {tx.users?.display_name ?? '(ไม่ระบุ)'} •
            <span className="font-mono ml-1">{tx.users?.line_uid?.slice(0, 12) ?? '—'}...</span>
          </div>

          {/* Meta */}
          <div className="flex items-center gap-3 text-[11px]" style={{ color: '#8a8270' }}>
            <span className="font-bold text-base" style={{ color: '#b8945a' }}>
              {tx.amount_baht}฿
            </span>
            <span>•</span>
            <span>{formatDateTime(tx.created_at)}</span>
            {tx.avg_confidence !== null && (
              <>
                <span>•</span>
                <span>AI {Math.round(tx.avg_confidence * 100)}%</span>
              </>
            )}
          </div>
        </div>

        {/* Actions (pending only) */}
        {isPending && (
          <div className="flex flex-col md:flex-row gap-1.5 flex-shrink-0">
            <button onClick={onApprove}
                    disabled={processing}
                    className="px-3 py-2 rounded-lg flex items-center justify-center gap-1 text-xs font-bold disabled:opacity-50"
                    style={{ background: '#6b8a4f', color: '#fff' }}>
              <CheckCircle2 size={14} />
              <span className="hidden md:inline">อนุมัติ</span>
            </button>
            <button onClick={onReject}
                    disabled={processing}
                    className="px-3 py-2 rounded-lg flex items-center justify-center gap-1 text-xs font-bold disabled:opacity-50"
                    style={{ background: '#b85450', color: '#fff' }}>
              <XCircle size={14} />
              <span className="hidden md:inline">ปฏิเสธ</span>
            </button>
          </div>
        )}

        {isAwaiting && (
          <span className="text-[10px] px-2 py-1 rounded-lg flex-shrink-0 self-start"
                style={{ background: 'rgba(0,0,0,0.04)', color: '#8a8270' }}>
            ยังไม่อัปโหลด
          </span>
        )}
      </div>
    </div>
  )
}
